import { useState, useEffect } from 'react';
import pageStyles from './TalentProfileEditPage.module.css';
import { useNavigate } from 'react-router-dom';
import { C, ff, t } from '../Dashboard/components/dashboardConstants';
import EditPhotoSection from './components/EditPhotoSection';
import EditBasicInfoSection from './components/EditBasicInfoSection';
import EditContactSection from './components/EditContactSection';
import EditFilmographySection from './components/EditFilmographySection';
import { dedupeAgainst, nextLocalWorkId } from '../../../shared/utils/workDedupe';
import { getMyTalentProfile, updateTalentProfile, setTalentVisibility, addTalentWork, updateTalentWork, addTalentSkill, deleteTalentSkill, uploadFile, addTalentMedia, deleteTalentMedia } from '../../../api';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import PortfolioAutoFillBox from '../../../shared/components/PortfolioAutoFillBox';
import { saveSkillOrder } from '../../../shared/utils/skillOrder';
import { useAuth } from '../../../contexts/AuthContext';
import TalentContentContainer from '../layout/TalentContentContainer';
import { resolveProfileImageUrl, markTalentProfileImageRemoved } from '../../../shared/utils/profilePlaceholders';

const GENDER_FROM_API = { MALE: '남성', FEMALE: '여성', OTHER: '기타' };
const GENDER_TO_API = { 남성: 'MALE', 여성: 'FEMALE', 기타: 'OTHER' };
const VISIBILITY_FROM_API = { PUBLIC: 'public', LINK_ONLY: 'link', PRIVATE: 'private' };
const VISIBILITY_TO_API = { public: 'PUBLIC', link: 'LINK_ONLY', private: 'PRIVATE' };
const PHOTO_ORDER_KEY = 'talent-photo-order';
const YELLOW_GRADIENT = 'linear-gradient(101.37deg, #FFD92B 0.67%, #FFB517 65.65%)';

export default function TalentProfileEditPage() {
  const navigate = useNavigate();
  const { profileId, setUserInfo, setProfile } = useAuth();

  const [name, setName] = useState('');
  const [englishName, setEnglishName] = useState('');
  const [stage, setStage] = useState('');
  const [gender, setGender] = useState('');
  const [birth, setBirth] = useState('');
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [location, setLocation] = useState('');
  const [bio, setBio] = useState('');
  const [agency, setAgency] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [instagram, setInstagram] = useState('');
  const [youtube, setYoutube] = useState('');
  const [customSkill, setCustomSkill] = useState('');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [initialSkillObjs, setInitialSkillObjs] = useState([]); // { id, name }
  const [photos, setPhotos] = useState([]);
  const [mainPhoto, setMainPhoto] = useState(null);
  const [mainPhotoMediaId, setMainPhotoMediaId] = useState(null);
  const [pendingMainFile, setPendingMainFile] = useState(null);       // 저장 전 대기 중인 새 파일
  const [pendingDeleteMainPhotoId, setPendingDeleteMainPhotoId] = useState(null); // 저장 전 삭제 대기
  const [works, setWorks] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [privacy, setPrivacy] = useState('public');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [autofillNotice, setAutofillNotice] = useState(null);

  const TABS = ['기본정보', '필모그래피'];

  const handlePortfolioParsed = (data) => {
    if (!data) return;
    if (data.name) setName(data.name);
    if (data.stage_name) setStage(data.stage_name);
    if (data.gender) setGender(data.gender === '남' ? '남성' : data.gender === '여' ? '여성' : data.gender);
    if (data.birth_date) setBirth(data.birth_date);
    if (data.height_cm) setHeight(String(data.height_cm));
    if (data.weight_kg) setWeight(String(data.weight_kg));
    if (data.residence) setLocation(data.residence);
    if (data.affiliation) setAgency(data.affiliation);
    if (data.bio) setBio(data.bio);
    if (data.instagram_url) setInstagram(data.instagram_url);
    if (data.youtube_url) setYoutube(data.youtube_url);
    if (Array.isArray(data.skills) && data.skills.length > 0) {
      setSelectedSkills(data.skills);
    }
    if (Array.isArray(data.works) && data.works.length > 0) {
      // 기존 목록을 갈아엎으면 서버에 있던 작품이 화면에서만 사라지고 그대로 남아
      // 파싱 결과가 통째로 새로 추가된다(= 중복). 교체가 아니라 병합해야 한다.
      setWorks(prev => {
        const parsed = data.works.map(w => ({
          year: w.year ? String(w.year) : '',
          title: w.title || '',
          company: w.production_company || w.channel || '',
          mediaType: w.category || '',
          role: w.role || '',
          videoUrl: '',
          stillcuts: [],
          _isNew: true,
        }));
        const { added, skipped } = dedupeAgainst(prev, parsed);
        let seed = nextLocalWorkId(prev);
        const rows = added.map(w => ({ ...w, id: seed-- }));
        setAutofillNotice(
          added.length === 0
            ? '불러온 작품이 모두 이미 등록되어 있어 추가하지 않았습니다.'
            : `작품 ${added.length}개를 추가했습니다.` + (skipped ? ` (중복 ${skipped}개 제외)` : '')
        );
        return [...prev, ...rows];
      });
    }
  };

  // 프로필 데이터 로드 (인증된 내 프로필 API 사용 — 공개 API는 contact 정보 제한)
  // profileId 의존 제거: checkSession은 profileId를 복원하지 않으므로 API 응답에서 직접 추출
  useEffect(() => {
    getMyTalentProfile()
      .then((data) => {
        if (data.id) setProfile(data.id);
        setName(data.name || '');
        setEnglishName(data.englishName || '');
        setStage(data.stageName || '');
        setGender(GENDER_FROM_API[data.gender] || '');
        setBirth(data.birthDate || '');
        setHeight(data.heightCm ? String(data.heightCm) : '');
        setWeight(data.weightKg ? String(data.weightKg) : '');
        setLocation(data.residence || '');
        setBio(data.bio || '');
        setEmail(data.contactEmail || '');
        setPhone(data.phone || '');
        setInstagram(data.instagramUrl || '');
        setYoutube(data.youtubeUrl || '');
        setAgency(data.agency || '');
        setPrivacy(VISIBILITY_FROM_API[data.visibility] || 'public');
        // medias: 대표사진 및 추가사진 로드 (FILMOGRAPHY 제외)
        const medias = data.medias || [];
        // FILMOGRAPHY media를 workId 기준으로 그룹핑 (작품당 최대 MAX_STILLCUTS장)
        const stillcutsByWorkId = {};
        medias
          .filter(m => m.photoType === 'FILMOGRAPHY' && m.workId && m.url)
          .forEach(m => {
            const key = m.workId;
            if (!stillcutsByWorkId[key]) stillcutsByWorkId[key] = [];
            stillcutsByWorkId[key].push({ mediaId: m.id, preview: m.url });
          });
        setWorks((data.works || []).map(w => {
          const grouped = stillcutsByWorkId[w.id];
          const stillcuts = grouped ?? (w.stillcutUrl ? [{ mediaId: w.stillcutMediaId ?? null, preview: w.stillcutUrl }] : []);
          return {
            id: w.id,
            year: String(w.year || w.releaseYear || ''),
            title: w.title || '',
            company: w.productionCompany || '',
            mediaType: w.category || '',
            role: w.role || w.roleName || '',
            videoUrl: w.videoUrl || '',
            stillcuts,
            videoFileName: null,
            videoFileId: null,
          };
        }));
        // skills: id + name 원본 보관, 표시용 name 배열 분리
        const skillObjs = (data.skills || []).map(s => ({ id: s.id, name: s.name || s.skillName || s })).filter(o => o.name);
        setInitialSkillObjs(skillObjs);
        setSelectedSkills(skillObjs.map(o => o.name));
        const closeUp = medias.find(m => m.photoType === 'PROFILE_CLOSE_UP' && m.url);
        setMainPhoto(closeUp?.url || null);
        setMainPhotoMediaId(closeUp?.id ?? null);
        const additional = medias
          .filter(m => m.mediaType === 'IMAGE' && m.photoType === 'PORTFOLIO' && m.url)
          .map(m => ({ id: m.id, url: m.url }));
        let orderedAdditional = additional;
        try {
          const raw = localStorage.getItem(PHOTO_ORDER_KEY);
          const all = raw ? JSON.parse(raw) : {};
          const savedOrder = all[String(profileId)] || [];
          if (savedOrder.length > 0) {
            const rank = new Map(savedOrder.map((key, index) => [String(key), index]));
            orderedAdditional = [...additional].sort((a, b) => {
              const aRank = rank.has(String(a.id ?? a.url)) ? rank.get(String(a.id ?? a.url)) : Number.MAX_SAFE_INTEGER;
              const bRank = rank.has(String(b.id ?? b.url)) ? rank.get(String(b.id ?? b.url)) : Number.MAX_SAFE_INTEGER;
              return aRank - bRank;
            });
          }
        } catch {}
        setPhotos(orderedAdditional);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!profileId || isSaving) return;
    setIsSaving(true);
    try {
      await updateTalentProfile({
        id: parseInt(profileId),
        name,
        englishName: englishName || undefined,
        stageName: stage || undefined,
        gender: GENDER_TO_API[gender] || undefined,
        birthDate: birth || undefined,
        heightCm: parseInt(height) || undefined,
        weightKg: parseInt(weight) || undefined,
        bio: bio || undefined,
        phone: phone || undefined,
        residence: location || undefined,
        contactEmail: email || undefined,
        agency: agency || undefined,
        instagramUrl: instagram ? instagram.replace(/^@+/, '') : undefined,
        youtubeUrl: youtube || undefined,
      });

      // 공개 설정이 변경된 경우 별도 API 호출
      const visibilityValue = VISIBILITY_TO_API[privacy];
      if (visibilityValue) {
        await setTalentVisibility(visibilityValue);
      }

      // 특기 저장
      const initialNames = initialSkillObjs.map(o => o.name);
      const toAdd = selectedSkills.filter(s => !initialNames.includes(s));
      const toDelete = initialSkillObjs.filter(o => !selectedSkills.includes(o.name));
      for (const skillName of toAdd) {
        await addTalentSkill({ talentProfileId: parseInt(profileId), skillTagId: null, customSkill: skillName });
      }
      for (const obj of toDelete) {
        await deleteTalentSkill(obj.id);
      }
      // 현재 순서 저장
      saveSkillOrder(profileId, selectedSkills);

      // 대표 사진 pending 처리
      if (pendingDeleteMainPhotoId) {
        try { await deleteTalentMedia(pendingDeleteMainPhotoId); } catch {}
        markTalentProfileImageRemoved(true);
      }
      if (pendingMainFile) {
        try {
          if (mainPhotoMediaId && !pendingDeleteMainPhotoId) {
            await deleteTalentMedia(mainPhotoMediaId).catch(() => {});
          }
          const { fileEntityId } = await uploadFile(pendingMainFile, 'IMAGE');
          await addTalentMedia({ talentProfileId: parseInt(profileId), fileEntityId, mediaType: 'IMAGE', photoType: 'PROFILE_CLOSE_UP' });
          markTalentProfileImageRemoved(false);
        } catch {}
      }

      // 저장 후 최신 프로필 이미지를 헤더/사이드바에 반영
      try {
        const latest = await getMyTalentProfile();
        const resolvedProfileImage = resolveProfileImageUrl(latest) || null;
        setUserInfo({ profileImage: resolvedProfileImage, name: latest.stageName || latest.name || undefined, stageName: latest.stageName || null, realName: latest.name || null });
      } catch {}

      // 필모그래피 저장
      for (let i = 0; i < works.length; i++) {
        const work = works[i];
        const workPayload = {
          title: work.title,
          year: parseInt(work.year) || undefined,
          productionCompany: work.company || undefined,
          category: work.mediaType || undefined,
          role: work.role || undefined,
          videoUrl: work.videoUrl || undefined,
          sortOrder: i,
        };
        try {
          let savedWorkId = work.id;
          if (work._isNew) {
            const created = await addTalentWork({ talentProfileId: parseInt(profileId), ...workPayload });
            savedWorkId = created?.id ?? null;
          } else if (work._isUpdated) {
            await updateTalentWork(work.id, workPayload);
          }

          // 삭제 대기 중인 기존 스틸컷 삭제
          for (const mediaId of work._deletedStillcutMediaIds || []) {
            await deleteTalentMedia(mediaId).catch(() => {});
          }

          // 새로 추가된 스틸컷 업로드 (신규/기존 작품 모두)
          if (savedWorkId) {
            for (const shot of work.stillcuts || []) {
              if (!shot.file) continue;
              try {
                const { fileEntityId } = await uploadFile(shot.file, 'IMAGE');
                await addTalentMedia({
                  talentProfileId: parseInt(profileId),
                  fileEntityId,
                  mediaType: 'IMAGE',
                  photoType: 'FILMOGRAPHY',
                  workId: savedWorkId,
                });
              } catch {
                // 스틸컷 업로드 실패는 전체 저장을 막지 않음
              }
            }
          }
        } catch {
          // 개별 작품 저장 실패는 무시하고 계속 진행
        }
      }

      navigate('/talent/profile');
    } catch {
      alert('저장에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LoadingSpinner size="md" />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <TalentContentContainer>
        {/* 헤더 */}
        <div style={{ padding: '20px 40px 16px', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <button
              onClick={() => navigate('/talent/profile')}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginBottom: '4px' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span style={t('14px', 400, C.sub)}>뒤로 가기</span>
            </button>
            <h1 style={{ ...t('32px', 700, C.bk), margin: 0 }}>프로필 편집</h1>
            <p style={{ ...t('14px', 400, C.sub), margin: '4px 0 0' }}>프로필 정보를 수정하고 저장하세요.</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {TABS.map((tab, i) => (
              <button key={tab} onClick={() => setActiveTab(i)} style={{
                padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                background: i === activeTab ? YELLOW_GRADIENT : C.g200,
                color: i === activeTab ? C.bk : C.sub,
                fontFamily: ff, fontSize: '15px', fontWeight: 500,
              }}>
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* 컨텐츠 */}
        <div style={{ padding: '0 40px 80px', flex: 1 }}>
          <div key={activeTab} className={pageStyles.tabContent}>
          {activeTab === 0 && (
            <>
              <PortfolioAutoFillBox onParsed={handlePortfolioParsed} />
              {autofillNotice && (
                <p style={{ fontSize: '13px', color: '#6b7280', margin: '8px 0 0' }}>{autofillNotice}</p>
              )}
              <EditPhotoSection
                talentProfileId={profileId}
                photos={photos}
                setPhotos={setPhotos}
                initialMainPhoto={mainPhoto}
                initialMainPhotoId={mainPhotoMediaId}
                onMainPhotoUploaded={() => {}}
                pendingMainFile={pendingMainFile}
                setPendingMainFile={setPendingMainFile}
                pendingDeleteMainPhotoId={pendingDeleteMainPhotoId}
                setPendingDeleteMainPhotoId={setPendingDeleteMainPhotoId}
              />
              <EditBasicInfoSection
                name={name} setName={setName}
                englishName={englishName} setEnglishName={setEnglishName}
                stage={stage} setStage={setStage}
                gender={gender} setGender={setGender}
                birth={birth} setBirth={setBirth}
                height={height} setHeight={setHeight}
                weight={weight} setWeight={setWeight}
                location={location} setLocation={setLocation}
                bio={bio} setBio={setBio}
                agency={agency} setAgency={setAgency}
                selectedSkills={selectedSkills} setSelectedSkills={setSelectedSkills}
                customSkill={customSkill} setCustomSkill={setCustomSkill}
                hideSkills
              />
              <EditContactSection
                email={email} setEmail={setEmail}
                phone={phone} setPhone={setPhone}
                instagram={instagram} setInstagram={setInstagram}
                youtube={youtube} setYoutube={setYoutube}
              />
              <EditBasicInfoSection
                name={name} setName={setName}
                stage={stage} setStage={setStage}
                gender={gender} setGender={setGender}
                birth={birth} setBirth={setBirth}
                height={height} setHeight={setHeight}
                weight={weight} setWeight={setWeight}
                location={location} setLocation={setLocation}
                bio={bio} setBio={setBio}
                agency={agency} setAgency={setAgency}
                selectedSkills={selectedSkills} setSelectedSkills={setSelectedSkills}
                customSkill={customSkill} setCustomSkill={setCustomSkill}
                skillsOnly
              />
            </>
          )}
          {activeTab === 1 && <EditFilmographySection talentProfileId={profileId} works={works} setWorks={setWorks} deferStillcutUpload={true} />}

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={handleSave}
              disabled={isSaving}
              style={{
                width: '320px', height: '60px',
                background: isSaving ? C.g200 : '#121212',
                border: 'none', borderRadius: '10px',
                cursor: isSaving ? 'default' : 'pointer',
                fontFamily: ff, fontSize: '18px', fontWeight: 600,
                color: C.white, letterSpacing: '-0.04em',
              }}
            >
              {isSaving ? '저장 중...' : '변경사항 저장'}
            </button>
          </div>
          </div>
        </div>
      </TalentContentContainer>
    </div>
  );
}
